/**
 * PublishDocumentPage - Admin Panel
 * Il Diario di Bordo - SaaS
 * 
 * Pagina per pubblicare un documento legale con opzioni:
 * - Impostare deadline
 * - Richiedere ri-firma
 * - Inviare notifiche
 * 
 * @version 1.0.0
 * @created Gennaio 2025
 */

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { adminApi } from '../services/api'
import {
  ChevronLeft,
  Send,
  AlertCircle,
  CheckCircle,
  Loader2,
  Calendar,
  Bell,
  Users,
  FileText,
  AlertTriangle
} from 'lucide-react'

export default function PublishDocumentPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  
  const [loading, setLoading] = useState(true)
  const [publishing, setPublishing] = useState(false)
  const [error, setError] = useState(null)
  
  const [document, setDocument] = useState(null)
  const [previousVersion, setPreviousVersion] = useState(null)
  const [affectedCount, setAffectedCount] = useState(0)
  
  const [options, setOptions] = useState({
    deadlineDays: 30,
    sendNotification: true,
    requireResignature: true
  })

  useEffect(() => {
    loadDocument()
  }, [id])

  const loadDocument = async () => {
    try {
      setLoading(true)
      const response = await adminApi.getLegalDocument(id)
      const doc = response.data.data.document
      
      if (doc.status !== 'draft') {
        setError('Solo i documenti in bozza possono essere pubblicati')
        return
      }
      
      setDocument(doc)
      
      // Trova versione precedente attiva
      const prevVersions = response.data.data.previousVersions || []
      const activePrev = prevVersions.find(v => v.status === 'active')
      setPreviousVersion(activePrev)
      
      // Conta utenti/organization interessati
      // Questo è un placeholder - in produzione faresti una chiamata API dedicata
      setAffectedCount(50) // Placeholder
      
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  const handlePublish = async () => {
    if (!confirm('Sei sicuro di voler pubblicare questo documento?')) {
      return
    }
    
    try {
      setPublishing(true)
      setError(null)
      
      await adminApi.publishLegalDocument(id, {
        deadlineDays: options.deadlineDays || null,
        sendNotification: options.sendNotification,
        requireResignature: options.requireResignature
      })
      
      // Redirect alla lista con messaggio successo
      navigate('/legal-documents', { 
        state: { success: `Documento "${document.title}" pubblicato con successo!` }
      })
      
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nella pubblicazione')
    } finally {
      setPublishing(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (!document) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-red-800 mb-2">
            Documento non trovato
          </h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/legal-documents')}
            className="text-primary-600 hover:underline"
          >
            Torna alla lista documenti
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/legal-documents')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Pubblica Documento
          </h1>
          <p className="text-gray-500">
            Conferma la pubblicazione di "{document.title}"
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Document Info */}
      <div className="bg-white border rounded-xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-primary-100 rounded-lg">
            <FileText className="h-6 w-6 text-primary-600" />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-lg">{document.title}</h2>
            <p className="text-gray-500">
              Versione {document.version} • {document.typeDisplayName}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Target: {document.targetAudience}
            </p>
          </div>
          <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
            Bozza
          </span>
        </div>
        
        {previousVersion && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-700">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm font-medium">
                Sostituirà la versione {previousVersion.version} attualmente attiva
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Options */}
      <div className="bg-white border rounded-xl p-6 mb-6 space-y-6">
        <h3 className="font-semibold">Opzioni Pubblicazione</h3>
        
        {/* Deadline */}
        <div>
          <label className="flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4 text-gray-500" />
            <span className="font-medium">Deadline per la firma</span>
          </label>
          <div className="grid grid-cols-5 gap-2">
            {[7, 14, 30, 60, 0].map(days => (
              <button
                key={days}
                type="button"
                onClick={() => setOptions({ ...options, deadlineDays: days })}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  options.deadlineDays === days
                    ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                    : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                }`}
              >
                {days === 0 ? 'Nessuna' : `${days} giorni`}
              </button>
            ))}
          </div>
          {options.deadlineDays > 0 && (
            <p className="text-sm text-gray-500 mt-2">
              Deadline: {new Date(Date.now() + options.deadlineDays * 24 * 60 * 60 * 1000).toLocaleDateString('it-IT', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          )}
        </div>
        
        {/* Notifications */}
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-gray-500" />
            <span className="font-medium">Notifiche</span>
          </label>
          
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
            <input
              type="checkbox"
              checked={options.sendNotification}
              onChange={(e) => setOptions({ ...options, sendNotification: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <p className="font-medium text-sm">Invia email di notifica</p>
              <p className="text-xs text-gray-500">
                Tutti gli utenti interessati riceveranno un'email
              </p>
            </div>
          </label>
        </div>
        
        {/* Re-signature */}
        {previousVersion && (
          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-500" />
              <span className="font-medium">Firme esistenti</span>
            </label>
            
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <input
                type="checkbox"
                checked={options.requireResignature}
                onChange={(e) => setOptions({ ...options, requireResignature: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <p className="font-medium text-sm">Richiedi nuova firma</p>
                <p className="text-xs text-gray-500">
                  Chi aveva firmato la versione precedente dovrà firmare nuovamente
                </p>
              </div>
            </label>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-blue-800 mb-3">Riepilogo</h3>
        <ul className="space-y-2 text-sm text-blue-700">
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            La versione {previousVersion?.version || 'precedente'} verrà archiviata
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Versione {document.version} diventerà attiva
          </li>
          {options.deadlineDays > 0 && (
            <li className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Deadline: {options.deadlineDays} giorni
            </li>
          )}
          {options.sendNotification && (
            <li className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Email di notifica verranno inviate
            </li>
          )}
          {options.requireResignature && previousVersion && (
            <li className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Tutte le firme precedenti verranno invalidate
            </li>
          )}
        </ul>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/legal-documents')}
          className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          Annulla
        </button>
        
        <button
          onClick={handlePublish}
          disabled={publishing}
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
        >
          {publishing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          Pubblica v{document.version}
        </button>
      </div>
    </div>
  )
}
